import { useDraggable } from '@dnd-kit/core';
import { clsx } from 'clsx';
import { Phone, Mail, Calendar } from 'lucide-react';
import { DocugenLifecycleBadge } from '../../../components/crm/DocugenLifecycleBadge';
import { PlaybookBadge } from './PlaybookBadge';
import { formatDateBR } from '../../../utils/format';

export interface LeadCardData {
  id: number;
  name: string;
  company: string | null;
  phone: string | null;
  email: string | null;
  nextFollowUpAt: string | null;
  dealValue: string | number | null;
  assignedUser?: { id: number; name: string } | null;
  integrationId?: number | null;
  externalCreatedAt?: string | null;
  externalDocsGenerated?: number | null;
  externalDocsUsed?: number | null;
  externalDocsLimit?: number | null;
  externalFirstDocAt?: string | null;
  externalLastActivityAt?: string | null;
  externalTriedLockedFeature?: boolean | null;
  externalTotalTokens?: number | null;
  playbookGroup?: string | null;
}

interface LeadCardProps {
  lead: LeadCardData;
  stageType?: string | null;
  onClick: () => void;
}

function formatMoney(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n) || n === 0) {
    return null;
  }
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export function LeadCard({ lead, stageType, onClick }: LeadCardProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.id.toString(),
  });

  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)` }
    : undefined;

  const overdue = lead.nextFollowUpAt && new Date(lead.nextFollowUpAt) < new Date();
  const money = formatMoney(lead.dealValue);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={onClick}
      className={clsx(
        'rounded-lg border border-border bg-surface p-3 cursor-pointer shadow-sm hover:shadow-md transition-all',
        isDragging && 'opacity-50 shadow-lg rotate-1 scale-[1.02]'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-ink-primary line-clamp-2 flex-1">{lead.name}</p>
        <div className="flex items-center gap-1">
          <PlaybookBadge group={lead.playbookGroup} />
          <DocugenLifecycleBadge lead={{ ...lead, stageType }} variant="compact" />
        </div>
      </div>
      {lead.company && <p className="text-xs text-ink-secondary mt-0.5 truncate">{lead.company}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-tertiary">
        {lead.phone && (
          <span className="inline-flex items-center gap-1">
            <Phone className="w-3 h-3" />
            {lead.phone}
          </span>
        )}
        {lead.email && (
          <span className="inline-flex items-center gap-1 truncate max-w-[140px]">
            <Mail className="w-3 h-3 shrink-0" />
            <span className="truncate">{lead.email}</span>
          </span>
        )}
        {lead.nextFollowUpAt && (
          <span
            className={clsx(
              'inline-flex items-center gap-1',
              overdue ? 'text-danger font-medium' : ''
            )}
          >
            <Calendar className="w-3 h-3" />
            {formatDateBR(lead.nextFollowUpAt)}
          </span>
        )}
        {money && <span className="ml-auto font-mono text-accent">{money}</span>}
      </div>
    </div>
  );
}
